/**
 * DealsPage — Issue #38, #49
 *
 * Deals board with three quality-band columns (Green / Yellow / Red),
 * a Watch List section (persisted to backend), and a Negotiations panel.
 *
 * Data is fetched from GET /deals/scored (ranked green/yellow/red).
 * Falls back gracefully when the endpoint is unavailable (404/network error).
 *
 * Issue #49: watch-list state is now persisted to the backend via:
 *  - GET /watchlist          → load on mount
 *  - POST /watchlist         → body { apn, reason? } — star on
 *  - DELETE /watchlist/:apn  → star off
 *  - POST /watchlist/:apn/follow-up  → body { note, nextFollowUpAt? }
 *
 * APN = DealCard.id stripped of "property:" prefix (confirmed live 2026-07-24).
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAINative } from "../providers/AINativeProvider.js";
import { PublicRecordDrawer } from "./deals/PublicRecordDrawer.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DealBand = "green" | "yellow" | "red";

export interface DealCard {
  id: string;
  address: string;
  /** 0–100 composite score */
  score: number;
  band: DealBand;
  /** Capitalisation rate as a decimal, e.g. 0.065 = 6.5% */
  cap_rate?: number;
  /** After-Repair Value in dollars */
  arv?: number;
  /** Asking / list price in dollars */
  price?: number;
  /** City / neighbourhood label */
  market?: string;
  /** Property type: SFR, Multi, Condo, etc. */
  property_type?: string;
  /** Days on market */
  dom?: number;
  /** True if user has starred this deal to Watch List */
  on_watch_list?: boolean;
  /** True if there's an active negotiation */
  in_negotiation?: boolean;
  /** Broker or contact name */
  broker?: string;
  /** ISO date string of the last activity */
  last_activity?: string;
  notes?: string;
}

export interface ScoredDealsResponse {
  /** Preferred shape: pre-mapped deal cards. */
  deals?: DealCard[];
  /** Live API shape from GET /deals/scored — scored property rows. */
  properties?: ScoredProperty[];
  total?: number;
  count?: number;
  as_of?: string;
}

/** A scored property as returned by the backend scoring plugin (#29). */
export interface ScoredProperty {
  entity_id: string;
  score: number;
  band: DealBand;
  reasons?: string[];
  address?: string;
  cap_rate?: number;
  arv?: number;
  price?: number;
  market?: string;
  property_type?: string;
  on_watch_list?: boolean;
  in_negotiation?: boolean;
  broker?: string;
  last_activity?: string;
}

/** Shape of a follow-up record returned by the backend. */
export interface FollowUpRecord {
  entity_id: string;
  watch_ref: string;
  property_ref: string;
  note: string;
  next_follow_up_at?: string | null;
  created_at: string;
}

/** Shape of a watchlist entry returned by GET /watchlist. */
export interface WatchlistEntry {
  entity_id: string;
  property_ref: string;
  apn: string;
  active: boolean;
  note: string | null;
  created_at: string;
  latest_follow_up: FollowUpRecord | null;
  next_follow_up_at: string | null;
}

/** Response shape for GET /watchlist. */
export interface WatchlistResponse {
  entries: WatchlistEntry[];
  count: number;
}

/** Map the live scoring-API property shape onto the DealCard the board renders. */
function propertyToDealCard(p: ScoredProperty): DealCard {
  return {
    id: p.entity_id,
    address: p.address ?? p.entity_id.replace(/^property:/, ""),
    score: p.score,
    band: p.band,
    ...(p.cap_rate !== undefined ? { cap_rate: p.cap_rate } : {}),
    ...(p.arv !== undefined ? { arv: p.arv } : {}),
    ...(p.price !== undefined ? { price: p.price } : {}),
    ...(p.market !== undefined ? { market: p.market } : {}),
    ...(p.property_type !== undefined ? { property_type: p.property_type } : {}),
    on_watch_list: p.on_watch_list ?? false,
    in_negotiation: p.in_negotiation ?? false,
    ...(p.broker !== undefined ? { broker: p.broker } : {}),
    ...(p.last_activity !== undefined ? { last_activity: p.last_activity } : {}),
    ...(p.reasons ? { notes: p.reasons.join(" · ") } : {}),
  };
}

/**
 * Extract the APN (assessor parcel number) from a DealCard id.
 * The backend watch-list uses APNs without the "property:" prefix.
 * e.g. "property:0123-456-789" → "0123-456-789"
 */
function apnFromId(id: string): string {
  return id.replace(/^property:/, "");
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function formatPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function arvSpread(arv: number, price: number): number {
  return arv - price;
}


function daysAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  return `${d}d ago`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Band config ──────────────────────────────────────────────────────────────

const BAND_CONFIG: Record<
  DealBand,
  { color: string; dimColor: string; borderColor: string; icon: string; label: string; range: string }
> = {
  green: {
    color: "var(--c-green)",
    dimColor: "rgba(34, 197, 94, 0.08)",
    borderColor: "rgba(34, 197, 94, 0.2)",
    icon: "▲",
    label: "Green",
    range: "Score 70–100",
  },
  yellow: {
    color: "var(--c-yellow)",
    dimColor: "rgba(245, 158, 11, 0.08)",
    borderColor: "rgba(245, 158, 11, 0.2)",
    icon: "◆",
    label: "Yellow",
    range: "Score 40–69",
  },
  red: {
    color: "var(--c-red)",
    dimColor: "rgba(239, 68, 68, 0.08)",
    borderColor: "rgba(239, 68, 68, 0.2)",
    icon: "▼",
    label: "Red",
    range: "Score 0–39",
  },
};

// ─── Mock fallback data ───────────────────────────────────────────────────────

const MOCK_DEALS: DealCard[] = [
  {
    id: "d1",
    address: "2847 Sycamore Ave, Oakland, CA 94605",
    score: 84,
    band: "green",
    cap_rate: 0.072,
    arv: 1_150_000,
    price: 820_000,
    market: "East Oakland",
    property_type: "Multi",
    dom: 6,
    on_watch_list: false,
    in_negotiation: false,
    broker: "Marcus Reid",
    last_activity: new Date(Date.now() - 2 * 86_400_000).toISOString(),
    notes: "Strong rent roll, seller motivated post-divorce.",
  },
  {
    id: "d2",
    address: "1209 Telegraph Ave, Berkeley, CA 94702",
    score: 78,
    band: "green",
    cap_rate: 0.068,
    arv: 980_000,
    price: 765_000,
    market: "Berkeley",
    property_type: "SFR",
    dom: 11,
    on_watch_list: true,
    in_negotiation: false,
    broker: "Sarah Kwan",
    last_activity: new Date(Date.now() - 86_400_000).toISOString(),
  },
  {
    id: "d3",
    address: "5500 Foothill Blvd, Fremont, CA 94538",
    score: 71,
    band: "green",
    cap_rate: 0.061,
    arv: 640_000,
    price: 520_000,
    market: "Fremont",
    property_type: "SFR",
    dom: 3,
    on_watch_list: false,
    in_negotiation: false,
    broker: "Tom Ellison",
    last_activity: new Date(Date.now() - 3 * 86_400_000).toISOString(),
  },
  {
    id: "d4",
    address: "3310 MacArthur Blvd, Oakland, CA 94602",
    score: 58,
    band: "yellow",
    cap_rate: 0.052,
    arv: 710_000,
    price: 640_000,
    market: "Glenview",
    property_type: "SFR",
    dom: 18,
    on_watch_list: true,
    in_negotiation: true,
    broker: "Diana Hall",
    last_activity: new Date(Date.now() - 4 * 3_600_000).toISOString(),
    notes: "Price drop possible — monitoring.",
  },
  {
    id: "d5",
    address: "4112 Grand Ave, Oakland, CA 94610",
    score: 47,
    band: "yellow",
    cap_rate: 0.049,
    arv: 880_000,
    price: 810_000,
    market: "Grand Lake",
    property_type: "Condo",
    dom: 27,
    on_watch_list: false,
    in_negotiation: false,
    broker: "Priya Mehta",
    last_activity: new Date(Date.now() - 7 * 86_400_000).toISOString(),
  },
  {
    id: "d6",
    address: "900 Pacific Ave, San Leandro, CA 94577",
    score: 31,
    band: "red",
    cap_rate: 0.038,
    arv: 490_000,
    price: 470_000,
    market: "San Leandro",
    property_type: "SFR",
    dom: 44,
    on_watch_list: false,
    in_negotiation: false,
    broker: "Jack Torres",
    last_activity: new Date(Date.now() - 14 * 86_400_000).toISOString(),
  },
  {
    id: "d7",
    address: "1800 Embarcadero, Oakland, CA 94606",
    score: 22,
    band: "red",
    cap_rate: 0.031,
    arv: 560_000,
    price: 545_000,
    market: "Fruitvale",
    property_type: "Multi",
    dom: 62,
    on_watch_list: false,
    in_negotiation: false,
    broker: "Lisa Ngo",
    last_activity: new Date(Date.now() - 21 * 86_400_000).toISOString(),
  },
];

// ─── Score ring component ──────────────────────────────────────────────────

interface ScoreRingProps {
  score: number;
  band: DealBand;
  size?: number;
}

function ScoreRing({ score, band, size = 44 }: ScoreRingProps) {
  const cfg = BAND_CONFIG[band];
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div
      className="deals-score-ring"
      style={{ width: size, height: size, flexShrink: 0 }}
      aria-label={`Score ${score}`}
    >
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--c-surface-3)"
          strokeWidth={3}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={cfg.color}
          strokeWidth={3}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span
        className="deals-score-ring__value"
        style={{ color: cfg.color, fontSize: size < 40 ? "10px" : "12px" }}
      >
        {score}
      </span>
    </div>
  );
}

// ─── Follow-up form component ─────────────────────────────────────────────────

interface FollowUpFormProps {
  dealId: string;
  onSubmit: (dealId: string, note: string, nextFollowUpAt?: string) => Promise<void>;
  onClose: () => void;
  submitting: boolean;
}

function FollowUpForm({ dealId, onSubmit, onClose, submitting }: FollowUpFormProps) {
  const [note, setNote] = useState("");
  const [nextDate, setNextDate] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    await onSubmit(dealId, note.trim(), nextDate || undefined);
  };

  return (
    <form
      className="follow-up-form"
      onSubmit={(e) => { void handleSubmit(e); }}
      aria-label="Record follow-up"
    >
      <div className="follow-up-form__field">
        <label className="follow-up-form__label" htmlFor={`follow-up-note-${dealId}`}>
          Note
        </label>
        <textarea
          id={`follow-up-note-${dealId}`}
          className="follow-up-form__textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What happened? Next steps..."
          rows={3}
          required
          aria-required="true"
        />
      </div>
      <div className="follow-up-form__field">
        <label className="follow-up-form__label" htmlFor={`follow-up-date-${dealId}`}>
          Next follow-up (optional)
        </label>
        <input
          id={`follow-up-date-${dealId}`}
          className="follow-up-form__date"
          type="date"
          value={nextDate}
          onChange={(e) => setNextDate(e.target.value)}
        />
      </div>
      <div className="follow-up-form__actions">
        <button
          type="submit"
          className="follow-up-form__submit"
          disabled={submitting || !note.trim()}
          aria-label="Save follow-up"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="follow-up-form__cancel"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Deal card component ───────────────────────────────────────────────────

interface DealCardProps {
  deal: DealCard;
  onToggleWatch: (id: string) => void;
  /** Only shown for watch-list entries */
  watchEntry?: WatchlistEntry | undefined;
  onRecordFollowUp?: ((dealId: string, note: string, nextFollowUpAt?: string) => Promise<void>) | undefined;
  /** Per-card watch-toggle error message */
  watchError?: string | undefined;
}

function DealCardItem({ deal, onToggleWatch, watchEntry, onRecordFollowUp, watchError }: DealCardProps) {
  const cfg = BAND_CONFIG[deal.band];
  const spread = deal.arv && deal.price ? arvSpread(deal.arv, deal.price) : null;
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);
  const [showPublicRecord, setShowPublicRecord] = useState(false);

  const handleFollowUpSubmit = async (
    dealId: string,
    note: string,
    nextFollowUpAt?: string,
  ) => {
    if (!onRecordFollowUp) return;
    setFollowUpSubmitting(true);
    try {
      await onRecordFollowUp(dealId, note, nextFollowUpAt);
      setShowFollowUpForm(false);
    } finally {
      setFollowUpSubmitting(false);
    }
  };

  return (
    <article
      className="deal-card"
      style={
        {
          "--band-color": cfg.color,
          "--band-dim": cfg.dimColor,
          "--band-border": cfg.borderColor,
        } as React.CSSProperties
      }
      aria-label={`Deal: ${deal.address}`}
    >
      {/* Left accent bar */}
      <div className="deal-card__accent" />

      <div className="deal-card__body">
        {/* Header row */}
        <div className="deal-card__header">
          <ScoreRing score={deal.score} band={deal.band} size={44} />
          <div className="deal-card__address-block">
            <p className="deal-card__address">{deal.address}</p>
            <div className="deal-card__meta-row">
              {deal.property_type && (
                <span className="deal-card__tag">{deal.property_type}</span>
              )}
              {deal.market && (
                <span className="deal-card__tag deal-card__tag--muted">{deal.market}</span>
              )}
              {deal.in_negotiation && (
                <span className="deal-card__tag deal-card__tag--negotiation">
                  Negotiating
                </span>
              )}
            </div>
          </div>
          <button
            className={`deal-card__watch-btn ${deal.on_watch_list ? "deal-card__watch-btn--active" : ""}`}
            onClick={() => onToggleWatch(deal.id)}
            aria-label={deal.on_watch_list ? "Remove from watch list" : "Add to watch list"}
            aria-pressed={deal.on_watch_list ?? false}
            title={deal.on_watch_list ? "Watching" : "Watch"}
          >
            {deal.on_watch_list ? "★" : "☆"}
          </button>
        </div>

        {/* Watch error */}
        {watchError && (
          <p className="deal-card__watch-error" role="alert">
            {watchError}
          </p>
        )}

        {/* Financials grid */}
        <div className="deal-card__financials">
          {deal.price !== undefined && (
            <div className="deal-card__fin-item">
              <span className="deal-card__fin-label">Price</span>
              <span className="deal-card__fin-value">{formatCurrency(deal.price)}</span>
            </div>
          )}
          {deal.arv !== undefined && (
            <div className="deal-card__fin-item">
              <span className="deal-card__fin-label">ARV</span>
              <span className="deal-card__fin-value">{formatCurrency(deal.arv)}</span>
            </div>
          )}
          {spread !== null && (
            <div className="deal-card__fin-item">
              <span className="deal-card__fin-label">Spread</span>
              <span
                className="deal-card__fin-value"
                style={{ color: spread >= 0 ? "var(--c-green)" : "var(--c-red)" }}
              >
                {spread >= 0 ? "+" : ""}
                {formatCurrency(spread)}
              </span>
            </div>
          )}
          {deal.cap_rate !== undefined && (
            <div className="deal-card__fin-item">
              <span className="deal-card__fin-label">Cap Rate</span>
              <span className="deal-card__fin-value">{formatPct(deal.cap_rate)}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="deal-card__footer">
          <div className="deal-card__footer-left">
            {deal.broker && (
              <span className="deal-card__broker" title="Broker">
                {deal.broker}
              </span>
            )}
            {deal.dom !== undefined && (
              <span className="deal-card__dom">{deal.dom}d on market</span>
            )}
          </div>
          {deal.last_activity && (
            <span className="deal-card__activity">{daysAgo(deal.last_activity)}</span>
          )}
        </div>

        {deal.notes && <p className="deal-card__notes">{deal.notes}</p>}

        {/* Public record — tax / zoning / permits / distress (SF DataSF) */}
        <button
          className="deal-card__pubrec-btn"
          onClick={() => setShowPublicRecord(true)}
          aria-label={`View public record for ${deal.address}`}
        >
          🏛 Public record
        </button>

        {/* Watch-list extras: last follow-up + record button */}
        {watchEntry && (
          <div className="deal-card__watchlist-extras">
            {watchEntry.latest_follow_up ? (
              <div className="deal-card__last-followup">
                <span className="deal-card__last-followup-label">Last follow-up</span>
                <span className="deal-card__last-followup-date">
                  {formatDate(watchEntry.latest_follow_up.created_at)}
                </span>
                <p className="deal-card__last-followup-note">
                  {watchEntry.latest_follow_up.note}
                </p>
                {watchEntry.next_follow_up_at && (
                  <span className="deal-card__next-followup">
                    Next: {formatDate(watchEntry.next_follow_up_at)}
                  </span>
                )}
              </div>
            ) : null}

            {onRecordFollowUp && !showFollowUpForm && (
              <button
                className="deal-card__followup-btn"
                onClick={() => setShowFollowUpForm(true)}
                aria-label={`Record follow-up for ${deal.address}`}
              >
                + Record follow-up
              </button>
            )}

            {showFollowUpForm && onRecordFollowUp && (
              <FollowUpForm
                dealId={deal.id}
                onSubmit={handleFollowUpSubmit}
                onClose={() => setShowFollowUpForm(false)}
                submitting={followUpSubmitting}
              />
            )}
          </div>
        )}
      </div>

      {showPublicRecord && (
        <PublicRecordDrawer
          blockLot={apnFromId(deal.id)}
          title={deal.address}
          onClose={() => setShowPublicRecord(false)}
        />
      )}
    </article>
  );
}

// ─── Band column ──────────────────────────────────────────────────────────────

interface BandColumnProps {
  band: DealBand;
  deals: DealCard[];
  onToggleWatch: (id: string) => void;
  watchErrors: Record<string, string>;
  /** Watch entries keyed by deal id — drives the inline follow-up affordance. */
  watchEntries: Record<string, WatchlistEntry>;
  onRecordFollowUp: (dealId: string, note: string, nextFollowUpAt?: string) => Promise<void>;
}

function BandColumn({
  band,
  deals,
  onToggleWatch,
  watchErrors,
  watchEntries,
  onRecordFollowUp,
}: BandColumnProps) {
  const cfg = BAND_CONFIG[band];

  return (
    <section
      className="deals-band"
      aria-label={`${cfg.label} band — ${deals.length} deal${deals.length !== 1 ? "s" : ""}`}
    >
      {/* Column header */}
      <div
        className="deals-band__header"
        style={
          {
            "--band-color": cfg.color,
            "--band-dim": cfg.dimColor,
            "--band-border": cfg.borderColor,
          } as React.CSSProperties
        }
      >
        <div className="deals-band__title-row">
          <span className="deals-band__icon" style={{ color: cfg.color }}>
            {cfg.icon}
          </span>
          <h2 className="deals-band__title" style={{ color: cfg.color }}>
            {cfg.label}
          </h2>
          <span className="deals-band__count">{deals.length}</span>
        </div>
        <span className="deals-band__range">{cfg.range}</span>
      </div>

      {/* Cards list */}
      <div className="deals-band__cards" role="list">
        {deals.length === 0 ? (
          <div className="deals-band__empty" aria-live="polite">
            <span className="deals-band__empty-icon">◌</span>
            <p>No {cfg.label.toLowerCase()} deals</p>
          </div>
        ) : (
          deals.map((deal) => (
            <div key={deal.id} role="listitem">
              <DealCardItem
                deal={deal}
                onToggleWatch={onToggleWatch}
                watchError={watchErrors[deal.id]}
                // Once a board card is watched, allow logging a follow-up in
                // place — no need to hunt for the card in the Watch List below.
                {...(deal.on_watch_list
                  ? { watchEntry: watchEntries[deal.id], onRecordFollowUp }
                  : {})}
              />
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ─── Watch list section ───────────────────────────────────────────────────────

interface WatchListProps {
  deals: DealCard[];
  onToggleWatch: (id: string) => void;
  watchEntries: Record<string, WatchlistEntry>;
  onRecordFollowUp: (dealId: string, note: string, nextFollowUpAt?: string) => Promise<void>;
  watchErrors: Record<string, string>;
}

function WatchList({ deals, onToggleWatch, watchEntries, onRecordFollowUp, watchErrors }: WatchListProps) {
  return (
    <section className="deals-watch-list" aria-label="Watch list">
      <div className="deals-section-header">
        <span className="deals-section-header__icon" style={{ color: "var(--c-accent)" }}>★</span>
        <h2 className="deals-section-header__title">Watch List</h2>
        <span className="deals-section-header__count">{deals.length}</span>
      </div>

      {deals.length === 0 ? (
        <div className="deals-empty-section" role="status">
          <p className="deals-empty-section__text">
            Star any deal to track it here.
          </p>
        </div>
      ) : (
        <div className="deals-watch-list__grid" role="list">
          {deals.map((deal) => (
            <div key={deal.id} role="listitem">
              <DealCardItem
                deal={deal}
                onToggleWatch={onToggleWatch}
                watchEntry={watchEntries[deal.id]}
                onRecordFollowUp={onRecordFollowUp}
                watchError={watchErrors[deal.id]}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Negotiations panel ───────────────────────────────────────────────────────

interface NegotiationsPanelProps {
  deals: DealCard[];
}

function NegotiationsPanel({ deals }: NegotiationsPanelProps) {
  return (
    <section className="deals-negotiations" aria-label="Active negotiations">
      <div className="deals-section-header">
        <span className="deals-section-header__icon" style={{ color: "var(--c-blue)" }}>◈</span>
        <h2 className="deals-section-header__title">Negotiations</h2>
        <span className="deals-section-header__count">{deals.length}</span>
      </div>

      {deals.length === 0 ? (
        <div className="deals-empty-section" role="status">
          <p className="deals-empty-section__text">No active negotiations.</p>
        </div>
      ) : (
        <div className="deals-negotiations__list" role="list">
          {deals.map((deal) => {
            const cfg = BAND_CONFIG[deal.band];
            const spread = deal.arv && deal.price ? arvSpread(deal.arv, deal.price) : null;
            return (
              <div key={deal.id} className="deals-neg-row" role="listitem">
                <ScoreRing score={deal.score} band={deal.band} size={36} />
                <div className="deals-neg-row__info">
                  <p className="deals-neg-row__address">{deal.address}</p>
                  <div className="deals-neg-row__meta">
                    <span style={{ color: cfg.color }} className="deals-neg-row__band">
                      {cfg.icon} {cfg.label}
                    </span>
                    {deal.price && (
                      <span className="deals-neg-row__stat">
                        {formatCurrency(deal.price)}
                      </span>
                    )}
                    {spread !== null && (
                      <span
                        className="deals-neg-row__stat"
                        style={{ color: spread >= 0 ? "var(--c-green)" : "var(--c-red)" }}
                      >
                        {spread >= 0 ? "+" : ""}
                        {formatCurrency(spread)} spread
                      </span>
                    )}
                    {deal.broker && (
                      <span className="deals-neg-row__stat deals-neg-row__stat--broker">
                        {deal.broker}
                      </span>
                    )}
                  </div>
                </div>
                {deal.last_activity && (
                  <span className="deals-neg-row__time">{daysAgo(deal.last_activity)}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="deals-loading" aria-busy="true" aria-label="Loading deals">
      <div className="deals-board">
        {(["green", "yellow", "red"] as DealBand[]).map((band) => (
          <div key={band} className="deals-band">
            <div
              className="deals-band__header"
              style={
                {
                  "--band-color": BAND_CONFIG[band].color,
                  "--band-dim": BAND_CONFIG[band].dimColor,
                  "--band-border": BAND_CONFIG[band].borderColor,
                } as React.CSSProperties
              }
            >
              <div className="deals-band__title-row">
                <span className="deals-band__icon" style={{ color: BAND_CONFIG[band].color }}>
                  {BAND_CONFIG[band].icon}
                </span>
                <h2 className="deals-band__title" style={{ color: BAND_CONFIG[band].color }}>
                  {BAND_CONFIG[band].label}
                </h2>
              </div>
              <span className="deals-band__range">{BAND_CONFIG[band].range}</span>
            </div>
            <div className="deals-band__cards">
              {[0, 1, 2].map((i) => (
                <div key={i} className="deal-card deal-card--skeleton">
                  <div className="deal-card__accent" />
                  <div className="deal-card__body">
                    <div className="skeleton-block skeleton-block--circle" />
                    <div className="skeleton-block skeleton-block--line" />
                    <div className="skeleton-block skeleton-block--line skeleton-block--short" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

interface ErrorStateProps {
  error: string;
  onRetry: () => void;
  usingFallback?: boolean;
}

function ErrorState({ error, onRetry, usingFallback }: ErrorStateProps) {
  return (
    <div className="deals-error" role="alert" aria-live="assertive">
      <div className="deals-error__icon">⚠</div>
      <p className="deals-error__title">Could not reach deals endpoint</p>
      <p className="deals-error__detail">{error}</p>
      {usingFallback && (
        <p className="deals-error__fallback">Showing sample data for preview.</p>
      )}
      <button className="deals-error__retry" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function DealsPage() {
  const { api } = useAINative();

  const [deals, setDeals] = useState<DealCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [asOf, setAsOf] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Optimistic watch state: maps deal.id -> boolean (undefined = use server state)
  const [watchOverrides, setWatchOverrides] = useState<Record<string, boolean>>({});

  // Per-card watch-toggle error messages (shown inline, cleared on next attempt)
  const [watchErrors, setWatchErrors] = useState<Record<string, string>>({});

  // Map of deal.id -> WatchlistEntry (drives latest_follow_up display)
  const [watchEntries, setWatchEntries] = useState<Record<string, WatchlistEntry>>({});

  // Stable ref to the api client so callbacks don't re-create on each render
  // (avoids infinite loops when test mocks return a fresh api object every call)
  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  });

  // ── Fetch /deals/scored ─────────────────────────────────────────────────────

  const fetchDeals = useCallback(async () => {
    // Cancel any in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setUsingFallback(false);

    try {
      const data = await apiRef.current.call<ScoredDealsResponse>("/deals/scored");
      // Accept both the pre-mapped {deals} shape and the live {properties} shape.
      const cards = data.deals ?? (data.properties ?? []).map(propertyToDealCard);
      setDeals(cards);
      setAsOf(data.as_of ?? null);
      setLoading(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      // Treat 404 / network failures gracefully — show sample data
      setError(msg);
      setDeals(MOCK_DEALS);
      setUsingFallback(true);
      setLoading(false);
    }
  }, []);

  // ── Fetch /watchlist ────────────────────────────────────────────────────────

  const fetchWatchlist = useCallback(async () => {
    try {
      const data = await apiRef.current.call<WatchlistResponse>("/watchlist");
      const entries = data.entries ?? [];
      // Build a map: deal.id (= "property:<apn>") -> WatchlistEntry
      const byId: Record<string, WatchlistEntry> = {};
      const overrides: Record<string, boolean> = {};
      for (const entry of entries) {
        if (entry.active) {
          byId[entry.property_ref] = entry;     // property_ref = "property:<apn>"
          overrides[entry.property_ref] = true;
        }
      }
      setWatchEntries(byId);
      // Seed overrides from server — but don't clobber any pending optimistic updates
      setWatchOverrides((prev) => {
        // Only set overrides for IDs not already in prev (prev may have in-flight changes)
        const next: Record<string, boolean> = { ...overrides };
        for (const id of Object.keys(prev)) {
          const val = prev[id];
          if (val !== undefined) {
            next[id] = val;
          }
        }
        return next;
      });
    } catch {
      // Watch-list load failure is non-fatal; board still renders without server state
    }
  }, []);

  useEffect(() => {
    void fetchDeals();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchDeals]);

  useEffect(() => {
    void fetchWatchlist();
  }, [fetchWatchlist]);

  // ── Toggle watch-list (write-through with optimistic update + revert) ───────

  const handleToggleWatch = useCallback(
    async (id: string) => {
      const currentWatchState =
        watchOverrides[id] !== undefined
          ? watchOverrides[id]
          : (deals.find((d) => d.id === id)?.on_watch_list ?? false);

      const nextState = !currentWatchState;

      // Clear any previous error for this card
      setWatchErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      // Optimistic update
      setWatchOverrides((prev) => ({ ...prev, [id]: nextState }));

      const apn = apnFromId(id);

      try {
        if (nextState) {
          // Star on → POST /watchlist
          await apiRef.current.call<WatchlistEntry>("/watchlist", {
            method: "POST",
            body: { apn },
          });
        } else {
          // Star off → DELETE /watchlist/:apn
          await apiRef.current.call<{ removed: boolean; apn: string }>(`/watchlist/${apn}`, {
            method: "DELETE",
          });
        }
        // Refresh watch entries to get latest server state (including follow-ups)
        await fetchWatchlist();
      } catch (err: unknown) {
        // Revert the optimistic update
        setWatchOverrides((prev) => ({ ...prev, [id]: currentWatchState }));
        const msg =
          err instanceof Error ? err.message : "Failed to update watch list";
        setWatchErrors((prev) => ({ ...prev, [id]: msg }));
      }
    },
    [deals, watchOverrides, fetchWatchlist],
  );

  // ── Record follow-up ─────────────────────────────────────────────────────────

  const handleRecordFollowUp = useCallback(
    async (dealId: string, note: string, nextFollowUpAt?: string) => {
      const apn = apnFromId(dealId);
      await apiRef.current.call<FollowUpRecord>(`/watchlist/${apn}/follow-up`, {
        method: "POST",
        body: {
          note,
          ...(nextFollowUpAt
            ? { nextFollowUpAt: new Date(nextFollowUpAt).toISOString() }
            : {}),
        },
      });
      // Refresh to pick up latest_follow_up in the GET /watchlist response
      await fetchWatchlist();
    },
    [fetchWatchlist],
  );

  // ── Merge remote deals with local watch-state overrides ─────────────────────

  const mergedDeals: DealCard[] = deals.map((d) => ({
    ...d,
    on_watch_list: watchOverrides[d.id] ?? d.on_watch_list ?? false,
  }));

  // Partition into bands
  const greenDeals = mergedDeals.filter((d) => d.band === "green");
  const yellowDeals = mergedDeals.filter((d) => d.band === "yellow");
  const redDeals = mergedDeals.filter((d) => d.band === "red");
  const watchListDeals = mergedDeals.filter((d) => d.on_watch_list);
  const negotiationDeals = mergedDeals.filter((d) => d.in_negotiation);

  return (
    <>
      {/* Inline page styles — scoped to DealsPage, matches global.css design tokens */}
      <style>{DEALS_STYLES}</style>

      <div className="deals-page">
        {/* Page header */}
        <header className="deals-page__header">
          <div className="deals-page__title-group">
            <h1 className="deals-page__title">Deals</h1>
            {asOf && !usingFallback && (
              <span className="deals-page__as-of">
                Scored {daysAgo(asOf)}
              </span>
            )}
            {usingFallback && (
              <span className="deals-page__sample-badge" role="note">
                Sample data — endpoint offline
              </span>
            )}
          </div>

          <div className="deals-page__header-actions">
            <button
              className="deals-page__refresh-btn"
              onClick={() => void fetchDeals()}
              disabled={loading}
              aria-label="Refresh deals"
              title="Refresh"
            >
              <span
                className={`deals-page__refresh-icon ${loading ? "deals-page__refresh-icon--spinning" : ""}`}
                aria-hidden="true"
              >
                ↺
              </span>
              Refresh
            </button>
          </div>
        </header>

        {/* Summary stat bar */}
        {!loading && (
          <div className="deals-stat-bar" aria-label="Deals summary">
            <div className="deals-stat-bar__item">
              <span className="deals-stat-bar__value" style={{ color: "var(--c-green)" }}>
                {greenDeals.length}
              </span>
              <span className="deals-stat-bar__label">Green</span>
            </div>
            <div className="deals-stat-bar__divider" />
            <div className="deals-stat-bar__item">
              <span className="deals-stat-bar__value" style={{ color: "var(--c-yellow)" }}>
                {yellowDeals.length}
              </span>
              <span className="deals-stat-bar__label">Yellow</span>
            </div>
            <div className="deals-stat-bar__divider" />
            <div className="deals-stat-bar__item">
              <span className="deals-stat-bar__value" style={{ color: "var(--c-red)" }}>
                {redDeals.length}
              </span>
              <span className="deals-stat-bar__label">Red</span>
            </div>
            <div className="deals-stat-bar__divider" />
            <div className="deals-stat-bar__item">
              <span className="deals-stat-bar__value" style={{ color: "var(--c-accent)" }}>
                {watchListDeals.length}
              </span>
              <span className="deals-stat-bar__label">Watching</span>
            </div>
            <div className="deals-stat-bar__divider" />
            <div className="deals-stat-bar__item">
              <span className="deals-stat-bar__value" style={{ color: "var(--c-blue)" }}>
                {negotiationDeals.length}
              </span>
              <span className="deals-stat-bar__label">Negotiating</span>
            </div>
          </div>
        )}

        {/* Error banner (when API is down but showing fallback) */}
        {error && usingFallback && (
          <div className="deals-offline-notice" role="alert">
            <span className="deals-offline-notice__icon">⚡</span>
            <span>Endpoint unavailable — showing sample data.</span>{" "}
            <button
              className="deals-offline-notice__retry"
              onClick={() => void fetchDeals()}
            >
              Try again
            </button>
          </div>
        )}

        {/* Error state (no fallback data) */}
        {error && !usingFallback && !loading && (
          <ErrorState error={error} onRetry={fetchDeals} />
        )}

        {/* Loading skeleton */}
        {loading && <LoadingSkeleton />}

        {/* Main board — three band columns */}
        {!loading && (
          <main className="deals-main-content">
            <div className="deals-board" aria-label="Deal bands">
              <BandColumn
                band="green"
                deals={greenDeals}
                onToggleWatch={(id) => { void handleToggleWatch(id); }}
                watchErrors={watchErrors}
                watchEntries={watchEntries}
                onRecordFollowUp={handleRecordFollowUp}
              />
              <BandColumn
                band="yellow"
                deals={yellowDeals}
                onToggleWatch={(id) => { void handleToggleWatch(id); }}
                watchErrors={watchErrors}
                watchEntries={watchEntries}
                onRecordFollowUp={handleRecordFollowUp}
              />
              <BandColumn
                band="red"
                deals={redDeals}
                onToggleWatch={(id) => { void handleToggleWatch(id); }}
                watchErrors={watchErrors}
                watchEntries={watchEntries}
                onRecordFollowUp={handleRecordFollowUp}
              />
            </div>

            {/* Below-board sections: Watch List + Negotiations side by side */}
            <div className="deals-lower-grid">
              <WatchList
                deals={watchListDeals}
                onToggleWatch={(id) => { void handleToggleWatch(id); }}
                watchEntries={watchEntries}
                onRecordFollowUp={handleRecordFollowUp}
                watchErrors={watchErrors}
              />
              <NegotiationsPanel deals={negotiationDeals} />
            </div>
          </main>
        )}
      </div>
    </>
  );
}

// ─── Scoped styles ────────────────────────────────────────────────────────────

const DEALS_STYLES = `
/* ── Deals page layout ─────────────────────────────────────── */
.deals-page {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 0;
}

/* ── Header ────────────────────────────────────────────────── */
.deals-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
}

.deals-page__title-group {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.deals-page__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(24px, 3vw, 34px);
  letter-spacing: -0.03em;
  color: var(--c-text-primary);
  line-height: 1;
}

.deals-page__as-of {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-text-muted);
}

.deals-page__sample-badge {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-yellow);
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  padding: 3px 8px;
  border-radius: var(--radius-sm);
}

.deals-page__header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.deals-page__refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--c-border-strong);
  background: var(--c-surface-1);
  color: var(--c-text-secondary);
  font-size: 12px;
  font-weight: 500;
  font-family: var(--font-body);
  cursor: pointer;
  transition: background var(--t-fast), color var(--t-fast);
}
.deals-page__refresh-btn:hover:not(:disabled) {
  background: var(--c-surface-2);
  color: var(--c-text-primary);
}
.deals-page__refresh-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.deals-page__refresh-icon {
  font-size: 14px;
  display: inline-block;
}
.deals-page__refresh-icon--spinning {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Stat bar ─────────────────────────────────────────────── */
.deals-stat-bar {
  display: flex;
  align-items: center;
  gap: 0;
  background: var(--c-surface-1);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  padding: 10px 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 4px;
}

.deals-stat-bar__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0 16px;
}

.deals-stat-bar__value {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 22px;
  line-height: 1;
  letter-spacing: -0.02em;
}

.deals-stat-bar__label {
  font-size: 10px;
  font-weight: 500;
  color: var(--c-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
}

.deals-stat-bar__divider {
  width: 1px;
  height: 32px;
  background: var(--c-border);
  flex-shrink: 0;
}

/* ── Offline notice ───────────────────────────────────────── */
.deals-offline-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.18);
  border-radius: var(--radius-sm);
  padding: 9px 14px;
  font-size: 12.5px;
  color: var(--c-text-secondary);
  margin-bottom: 20px;
}
.deals-offline-notice__icon {
  color: var(--c-yellow);
  flex-shrink: 0;
}
.deals-offline-notice__retry {
  color: var(--c-accent);
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  background: none;
  border: none;
  padding: 0;
}
.deals-offline-notice__retry:hover {
  color: var(--c-accent-hover);
}

/* ── Error state ──────────────────────────────────────────── */
.deals-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 56px 24px;
  text-align: center;
}
.deals-error__icon {
  font-size: 28px;
  color: var(--c-red);
  opacity: 0.6;
}
.deals-error__title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: var(--c-text-primary);
}
.deals-error__detail {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-text-muted);
  max-width: 400px;
}
.deals-error__fallback {
  font-size: 12px;
  color: var(--c-yellow);
}
.deals-error__retry {
  margin-top: 8px;
  padding: 7px 20px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--c-border-strong);
  background: var(--c-surface-1);
  color: var(--c-text-secondary);
  font-size: 12px;
  font-family: var(--font-body);
  cursor: pointer;
  transition: background var(--t-fast);
}
.deals-error__retry:hover {
  background: var(--c-surface-2);
  color: var(--c-text-primary);
}

/* ── Main content layout ──────────────────────────────────── */
.deals-main-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* ── Board (three columns) ────────────────────────────────── */
.deals-board {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: start;
}

@media (max-width: 900px) {
  .deals-board {
    grid-template-columns: 1fr;
  }
}

/* ── Band column ──────────────────────────────────────────── */
.deals-band {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: var(--c-surface-0);
  border: 1px solid var(--c-border);
  border-top: 2px solid var(--band-color, var(--c-border));
  border-radius: var(--radius-md);
  overflow: hidden;
}

.deals-band__header {
  padding: 14px 16px 10px;
  background: var(--band-dim, var(--c-surface-1));
  border-bottom: 1px solid var(--band-border, var(--c-border));
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.deals-band__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.deals-band__icon {
  font-size: 10px;
}

.deals-band__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.deals-band__count {
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--c-text-muted);
  background: var(--c-surface-2);
  padding: 2px 7px;
  border-radius: 99px;
}

.deals-band__range {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-muted);
  letter-spacing: 0.04em;
}

.deals-band__cards {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.deals-band__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 20px;
  color: var(--c-text-muted);
  font-size: 12.5px;
  text-align: center;
}
.deals-band__empty-icon {
  font-size: 20px;
  opacity: 0.4;
}

/* ── Deal card ─────────────────────────────────────────────── */
.deal-card {
  display: flex;
  border-bottom: 1px solid var(--c-border);
  position: relative;
  transition: background var(--t-fast);
  cursor: default;
}
.deal-card:last-child {
  border-bottom: none;
}
.deal-card:hover {
  background: var(--c-surface-1);
}

.deal-card__accent {
  width: 3px;
  flex-shrink: 0;
  background: var(--band-color, var(--c-text-muted));
  opacity: 0.5;
}

.deal-card__body {
  flex: 1;
  min-width: 0;
  padding: 12px 14px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.deal-card__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.deal-card__address-block {
  flex: 1;
  min-width: 0;
}

.deal-card__address {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--c-text-primary);
  line-height: 1.4;
  word-break: break-word;
}

.deal-card__meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.deal-card__tag {
  font-family: var(--font-mono);
  font-size: 10px;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  background: var(--c-surface-2);
  color: var(--c-text-secondary);
  border: 1px solid var(--c-border);
}
.deal-card__tag--muted {
  opacity: 0.7;
}
.deal-card__tag--negotiation {
  color: var(--c-blue);
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.2);
}

.deal-card__watch-btn {
  font-size: 16px;
  color: var(--c-text-muted);
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  transition: color var(--t-fast), transform var(--t-fast);
  margin-top: -1px;
}
.deal-card__watch-btn:hover {
  color: var(--c-accent);
  transform: scale(1.15);
}
.deal-card__watch-btn--active {
  color: var(--c-accent);
}

/* Watch toggle error */
.deal-card__watch-error {
  font-size: 11px;
  color: var(--c-red, #ef4444);
  font-family: var(--font-mono);
  padding: 4px 6px;
  background: rgba(239, 68, 68, 0.06);
  border: 1px solid rgba(239, 68, 68, 0.15);
  border-radius: var(--radius-sm);
}

/* Score ring */
.deals-score-ring {
  position: relative;
  flex-shrink: 0;
}
.deals-score-ring__value {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-weight: 500;
  line-height: 1;
}

/* Financials */
.deal-card__financials {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px 8px;
}
.deal-card__fin-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.deal-card__fin-label {
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--c-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.deal-card__fin-value {
  font-size: 12px;
  font-weight: 500;
  color: var(--c-text-primary);
  font-family: var(--font-mono);
}

/* Footer */
.deal-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.deal-card__footer-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.deal-card__broker {
  font-size: 11px;
  color: var(--c-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.deal-card__dom {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-muted);
  white-space: nowrap;
}
.deal-card__activity {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.deal-card__notes {
  font-size: 11px;
  color: var(--c-text-muted);
  font-style: italic;
  line-height: 1.5;
  border-top: 1px solid var(--c-border);
  padding-top: 8px;
}

/* ── Watch-list extras (follow-ups) ───────────────────────── */
.deal-card__watchlist-extras {
  border-top: 1px solid var(--c-border);
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.deal-card__last-followup {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.deal-card__last-followup-label {
  font-family: var(--font-mono);
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--c-text-muted);
}
.deal-card__last-followup-date {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-secondary);
}
.deal-card__last-followup-note {
  font-size: 11.5px;
  color: var(--c-text-primary);
  line-height: 1.5;
}
.deal-card__next-followup {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-accent);
}

.deal-card__followup-btn {
  align-self: flex-start;
  font-size: 11px;
  font-family: var(--font-mono);
  color: var(--c-accent);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.deal-card__followup-btn:hover {
  color: var(--c-accent-hover, var(--c-accent));
}

/* ── Follow-up inline form ────────────────────────────────── */
.follow-up-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--c-surface-1);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
}
.follow-up-form__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.follow-up-form__label {
  font-family: var(--font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--c-text-muted);
}
.follow-up-form__textarea {
  font-size: 12px;
  font-family: var(--font-body);
  color: var(--c-text-primary);
  background: var(--c-surface-0);
  border: 1px solid var(--c-border-strong);
  border-radius: var(--radius-sm);
  padding: 7px 9px;
  resize: vertical;
  min-height: 64px;
}
.follow-up-form__textarea:focus {
  outline: none;
  border-color: var(--c-accent);
}
.follow-up-form__date {
  font-size: 12px;
  font-family: var(--font-mono);
  color: var(--c-text-primary);
  background: var(--c-surface-0);
  border: 1px solid var(--c-border-strong);
  border-radius: var(--radius-sm);
  padding: 5px 9px;
}
.follow-up-form__actions {
  display: flex;
  gap: 8px;
}
.follow-up-form__submit {
  padding: 5px 14px;
  border-radius: var(--radius-sm);
  border: none;
  background: var(--c-accent);
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  font-family: var(--font-body);
  cursor: pointer;
  transition: opacity var(--t-fast);
}
.follow-up-form__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.follow-up-form__cancel {
  padding: 5px 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--c-border-strong);
  background: var(--c-surface-1);
  color: var(--c-text-secondary);
  font-size: 11.5px;
  font-family: var(--font-body);
  cursor: pointer;
}

/* ── Skeleton loading cards ───────────────────────────────── */
.deal-card--skeleton {
  pointer-events: none;
}
.deal-card--skeleton .deal-card__body {
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
}
.skeleton-block {
  background: var(--c-surface-2);
  border-radius: var(--radius-sm);
  animation: skeleton-pulse 1.4s ease-in-out infinite;
}
.skeleton-block--circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}
.skeleton-block--line {
  height: 12px;
  flex: 1;
}
.skeleton-block--short {
  flex: 0.6;
  height: 10px;
}
@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
}

/* ── Lower grid (watch list + negotiations) ───────────────── */
.deals-lower-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

@media (max-width: 768px) {
  .deals-lower-grid {
    grid-template-columns: 1fr;
  }
}

/* ── Section header ───────────────────────────────────────── */
.deals-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  margin-bottom: 0;
  border-bottom: 1px solid var(--c-border);
}
.deals-section-header__icon {
  font-size: 13px;
}
.deals-section-header__title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-text-primary);
}
.deals-section-header__count {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-muted);
  background: var(--c-surface-2);
  padding: 2px 7px;
  border-radius: 99px;
  margin-left: auto;
}

/* ── Watch list ───────────────────────────────────────────── */
.deals-watch-list {
  background: var(--c-surface-0);
  border: 1px solid var(--c-border);
  border-top: 2px solid var(--c-accent);
  border-radius: var(--radius-md);
  overflow: hidden;
  padding: 14px 16px 0;
}
.deals-watch-list__grid {
  margin: 0 -16px;
}

/* ── Empty section ────────────────────────────────────────── */
.deals-empty-section {
  padding: 28px 0 14px;
  text-align: center;
}
.deals-empty-section__text {
  font-size: 12.5px;
  color: var(--c-text-muted);
}

/* ── Negotiations ─────────────────────────────────────────── */
.deals-negotiations {
  background: var(--c-surface-0);
  border: 1px solid var(--c-border);
  border-top: 2px solid var(--c-blue);
  border-radius: var(--radius-md);
  overflow: hidden;
  padding: 14px 16px;
}

.deals-negotiations__list {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 -16px;
}

.deals-neg-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid var(--c-border);
  transition: background var(--t-fast);
}
.deals-neg-row:hover {
  background: var(--c-surface-1);
}

.deals-neg-row__info {
  flex: 1;
  min-width: 0;
}
.deals-neg-row__address {
  font-size: 12px;
  font-weight: 500;
  color: var(--c-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.deals-neg-row__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}
.deals-neg-row__band {
  font-family: var(--font-mono);
  font-size: 10px;
}
.deals-neg-row__stat {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-secondary);
}
.deals-neg-row__stat--broker {
  color: var(--c-text-muted);
}
.deals-neg-row__time {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--c-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Public-record button + drawer (#54) ──────────────────────────── */
.deal-card__pubrec-btn {
  align-self: flex-start;
  font-size: 11px;
  font-family: var(--font-mono);
  color: var(--c-accent);
  background: var(--c-accent-dim, rgba(245,166,35,0.08));
  border: 1px solid rgba(245,166,35,0.25);
  border-radius: var(--radius-sm);
  padding: 4px 9px;
  cursor: pointer;
  transition: background var(--t-fast), border-color var(--t-fast);
}
.deal-card__pubrec-btn:hover {
  background: rgba(245,166,35,0.14);
  border-color: var(--c-accent);
}

.pubrec-drawer { position: fixed; inset: 0; z-index: 200; display: flex; justify-content: flex-end; }
.pubrec-drawer__backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(2px); }
.pubrec-drawer__panel {
  position: relative; z-index: 1; width: min(440px, 92vw); height: 100%;
  background: var(--c-surface-0); border-left: 1px solid var(--c-border-strong);
  display: flex; flex-direction: column; box-shadow: -12px 0 40px rgba(0,0,0,0.45);
  overflow-y: auto;
}
.pubrec-drawer__header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 18px 20px; border-bottom: 1px solid var(--c-border);
  position: sticky; top: 0; background: var(--c-surface-0);
}
.pubrec-drawer__eyebrow {
  font-family: var(--font-mono); font-size: 10px; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--c-accent);
}
.pubrec-drawer__title { font-size: 16px; font-weight: 600; color: var(--c-text-primary); margin: 4px 0 2px; }
.pubrec-drawer__blocklot { font-family: var(--font-mono); font-size: 11px; color: var(--c-text-muted); }
.pubrec-drawer__close {
  font-size: 22px; line-height: 1; color: var(--c-text-muted); background: none;
  border: none; cursor: pointer; padding: 0 4px;
}
.pubrec-drawer__close:hover { color: var(--c-text-primary); }
.pubrec-drawer__body { padding: 16px 20px 32px; display: flex; flex-direction: column; gap: 22px; }
.pubrec-drawer__loading, .pubrec-drawer__error { padding: 24px 0; font-size: 13px; color: var(--c-text-muted); }
.pubrec-drawer__error { color: var(--c-red); }

.pubrec-section__title {
  font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--c-text-secondary); margin-bottom: 10px; display: flex; align-items: center; gap: 8px;
}
.pubrec-count {
  font-family: var(--font-mono); font-size: 10px; color: var(--c-text-muted);
  background: var(--c-surface-2); padding: 1px 7px; border-radius: 99px;
}
.pubrec-badge { font-family: var(--font-mono); font-size: 10px; padding: 1px 7px; border-radius: 99px; }
.pubrec-badge--active { color: var(--c-green); background: rgba(34,197,94,0.1); }
.pubrec-badge--warn { color: var(--c-red); background: rgba(239,68,68,0.1); }
.pubrec-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.pubrec-stat { display: flex; flex-direction: column; gap: 2px; }
.pubrec-stat__label {
  font-family: var(--font-mono); font-size: 9.5px; text-transform: uppercase;
  letter-spacing: 0.05em; color: var(--c-text-muted);
}
.pubrec-stat__value { font-size: 13px; color: var(--c-text-primary); }
.pubrec-list { display: flex; flex-direction: column; gap: 0; }
.pubrec-list__item {
  display: flex; align-items: baseline; gap: 8px; padding: 7px 0;
  border-top: 1px solid var(--c-border); font-size: 12px;
}
.pubrec-list__main { flex: 1; min-width: 0; color: var(--c-text-primary); }
.pubrec-list__meta { font-family: var(--font-mono); font-size: 10px; color: var(--c-text-muted); white-space: nowrap; }
.pubrec-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--c-text-muted); flex-shrink: 0; align-self: center; }
.pubrec-dot--active { background: var(--c-green); }
.pubrec-dot--warn { background: var(--c-red); }
.pubrec-empty { font-size: 12px; color: var(--c-text-muted); font-style: italic; }
.pubrec-note { font-size: 11px; color: var(--c-text-muted); border-top: 1px solid var(--c-border); padding-top: 12px; }
`;
